import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiCall } from '@/utils/apiAxios';
import { Executive, AgentsOptions, SellersAgentOptions } from '@/types/agent/agent';
import { useRouter } from 'vue-router';
import { img } from '@/utils/img';
import { set } from 'lodash';
import { catalogIsFresh, catalogTouch } from '@/utils/catalogCache';
import { safeStorage } from '@/utils/safeStorage';

export const useAgentStore = defineStore('agent', () => {
    const agentsSelected = ref<AgentsOptions | null>(null);
    const agentsOptions = ref<AgentsOptions[]>([]);
    const executive = ref<Executive | null>(null);
    const sellersOptions = ref<SellersAgentOptions[] | null>(null);
    const isAgent = ref(safeStorage.getItem('isAgent') === 'true');
    const agentHash = ref('');
    const groupsAgent = ref([]);

    const userHasSellers = computed(() => {
        if (!sellersOptions.value) return false;
        return sellersOptions.value.length > 0;
    });

    const agentFullName = computed(() => {
        return `${agentsSelected.value?.first_name || ''} ${agentsSelected.value?.last_name || ''}`.trim();
    });

    const agentImg = computed(() => {
        if (agentsSelected.value?.image_url) {
            return img(agentsSelected.value.image_url); // Retorna la URL de la imagen si existe.
        }

        // Si no hay `image_url`, toma las iniciales.
        const firstnameInitial = agentsSelected.value?.first_name.charAt(0) || 'N';
        const lastnameInitial = agentsSelected.value?.last_name.charAt(0) || 'A';

        return `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`; // Combina las iniciales y las convierte a mayúsculas.
    });

    const userHasMultipleSellers = computed(() => {
        if (!sellersOptions.value) return false;
        return sellersOptions.value.length > 1;
    });

    const router = useRouter();

    const resetAllVariables = () => {
        agentsSelected.value = null;
        agentsOptions.value = [];
        executive.value = null;
        isAgent.value = false;
        agentHash.value = '';
        groupsAgent.value = [];
    };

    const getInfoAgent = async () => {
        // Leer clave temporal de la URL si existe
        const params = new URLSearchParams(window.location.search);
        const tempKey = params.get('tempKey');

        if (tempKey) {
            const agentHashTemp = safeStorage.getItem(tempKey);
            const impersonateLevel = safeStorage.getItem(`${tempKey}_level`);

            if (agentHashTemp) {
                safeStorage.setItem('agentHash', agentHashTemp, 'session');
                safeStorage.removeItem(tempKey);
            }

            if (impersonateLevel) {
                safeStorage.setItem('impersonateLevel', impersonateLevel, 'session');
                safeStorage.removeItem(`${tempKey}_level`);
            }
        }

        const storedAgentsOptions = safeStorage.getItem('agentsOptions');
        if (storedAgentsOptions && catalogIsFresh('agentsOptions')) {
            agentsOptions.value = JSON.parse(storedAgentsOptions);
            const agentSelelected = agentsOptions.value.find((agent) => agent.hash === safeStorage.getItem('agentHash', 'session'));
            if (agentSelelected) {
                setAgent(agentSelelected);
            } else {
                setAgent(agentsOptions.value[0]);
            }
        } else {
            await getInfoAgentGet();
        }
    };

    const getInfoAgentGet = async () => {
        const data = {};

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/users/get_agents`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            agentsOptions.value = response.response.agents;
            isAgent.value = response.response.is_agent;

            safeStorage.setItem('agentsOptions', JSON.stringify(agentsOptions.value));
            safeStorage.setItem('isAgent', response.response.is_agent);
            catalogTouch('agentsOptions');

            const agentHash = safeStorage.getItem('agentHash', 'session');
            if (agentHash) {
                setAgentHash(agentHash);
            } else {
                setAgent(agentsOptions.value[0]);
            }
        }
    };

    const getSellersAgentApi = async () => {
        const data = {
            agent_hash: agentHash.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/users/get_sellers_agent`,
            data: data
        });

        const response = await request;
        if (response.status == 1) {
            sellersOptions.value = response.response.sellers;

            safeStorage.setItem('sellersOptions', JSON.stringify(sellersOptions.value), 'session');
        }
    };

    const getSellersAgent = async () => {
        const storedSellersOptions = safeStorage.getItem('sellersOptions', 'session');
        if (storedSellersOptions) {
            sellersOptions.value = JSON.parse(storedSellersOptions);
            return;
        }

        await getSellersAgentApi();
    };

    const selectMainAgent = (agentHash: string) => {
        const tempKey = `agentHashTemp_${Date.now()}`; // clave única por pestaña
        safeStorage.setItem(tempKey, agentHash);
        const level = isAgent.value ? '2' : '0';
        safeStorage.setItem(`${tempKey}_level`, level);

        // Abrir nueva pestaña con clave temporal en la URL
        window.open(`/private/dashboard/main?tempKey=${tempKey}`, '_blank');
    };

    const setAgent = (agent: any) => {
        agentsSelected.value = agent;
        setAgentHash(agent.hash);
    };

    const setAgentHash = async (agentHashValue: string) => {
        agentHash.value = agentHashValue;
        safeStorage.setItem('agentHash', agentHashValue, 'session');
        await getInfoExecutive();
        getSellersAgent();
    };

    const getInfoExecutive = async () => {
        const sessionStorageExecutive = safeStorage.getItem('executive', 'session');
        if (sessionStorageExecutive !== null) {
            executive.value = JSON.parse(sessionStorageExecutive) as Executive;

            return;
        }

        const data = {
            agent_hash: agentHash.value
        };

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/users/get_executive`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            executive.value = response.response.executive;
            safeStorage.setItem('executive', JSON.stringify(executive.value), 'session');
        } else {
            router.push({
                name: 'noExecutive'
            });
        }

        return;
    };

    return {
        agentsSelected,
        agentImg,
        agentsOptions,
        executive,
        isAgent,
        agentHash,
        groupsAgent,
        sellersOptions,
        userHasSellers,
        userHasMultipleSellers,
        agentFullName,
        resetAllVariables,
        getInfoAgent,
        getInfoAgentGet,
        selectMainAgent,
        getInfoExecutive,
        getSellersAgent
    };
});
